import { useEffect, useMemo, useState } from "react";
import { ArrowLeftRight, TrendingUp, TrendingDown } from "lucide-react";

const RATES_URL = "https://open.er-api.com/v6/latest/USD";
const FRANKFURTER = "https://api.frankfurter.dev/v2";

const CCY_NAMES = {
  USD: "US Dollar", NGN: "Nigerian Naira", KES: "Kenyan Shilling", GHS: "Ghanaian Cedi",
  ZAR: "South African Rand", XOF: "West African CFA Franc", XAF: "Central African CFA Franc",
  EGP: "Egyptian Pound", MAD: "Moroccan Dirham", TZS: "Tanzanian Shilling", UGX: "Ugandan Shilling",
  RWF: "Rwandan Franc", EUR: "Euro", GBP: "British Pound", AED: "UAE Dirham",
  INR: "Indian Rupee", CNY: "Chinese Yuan", CAD: "Canadian Dollar", AUD: "Australian Dollar",
};
const CCYS = Object.keys(CCY_NAMES);

const ECB_CODES = ["USD", "EUR", "GBP", "AUD", "BGN", "BRL", "CAD", "CHF", "CNY", "CZK", "DKK", "HKD", "HUF", "IDR", "ILS", "INR", "ISK", "JPY", "KRW", "MXN", "MYR", "NOK", "NZD", "PHP", "PLN", "RON", "SEK", "SGD", "THB", "TRY", "ZAR"];

const selectCls =
  "rounded-lg border border-[#E2E8F0] bg-white px-2.5 py-2 text-sm font-semibold text-[#0F172A] outline-none transition-colors focus:border-[#2E7CD6] focus:ring-2 focus:ring-[#2E7CD6]/15";

const fmt = (n, d = 2) => n.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
const rateFmt = (n) => fmt(n, n >= 100 ? 2 : 4);
const dayFmt = (d) => d.toISOString().slice(0, 10);

const TrendChart = ({ from, to, ready }) => {
  const [points, setPoints] = useState(null);
  const supported = ECB_CODES.includes(from) && ECB_CODES.includes(to);

  useEffect(() => {
    if (!ready || !supported || from === to) {
      setPoints(null);
      return;
    }
    const controller = new AbortController();
    const end = new Date();
    const start = new Date(Date.now() - 30 * 86400000);
    (async () => {
      try {
        const res = await fetch(`${FRANKFURTER}/rates?from=${dayFmt(start)}&to=${dayFmt(end)}&base=${from}&quotes=${to}&providers=ECB`, {
          signal: controller.signal,
          headers: { Accept: "application/json" },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const rows = await res.json();
        if (!Array.isArray(rows)) throw new Error("Unexpected response");
        const valid = rows
          .filter((r) => typeof r?.date === "string" && Number.isFinite(r?.rate) && r.rate > 0)
          .sort((a, b) => a.date.localeCompare(b.date));
        setPoints(valid.length >= 2 ? valid : null);
      } catch {
        setPoints(null);
      }
    })();
    return () => controller.abort();
  }, [from, to, ready, supported]);

  const chart = useMemo(() => {
    if (!points) return null;
    const rates = points.map((p) => p.rate);
    const min = Math.min(...rates);
    const max = Math.max(...rates);
    const span = max - min || 1;
    const W = 560;
    const H = 56;
    const coords = rates.map((r, i) => `${(i / (rates.length - 1)) * W},${H - 6 - ((r - min) / span) * (H - 12)}`);
    const change = ((rates[rates.length - 1] - rates[0]) / rates[0]) * 100;
    return { line: coords.join(" "), area: `0,${H} ${coords.join(" ")} ${W},${H}`, change };
  }, [points]);

  if (!supported || !chart) return null;

  const up = chart.change >= 0;
  return (
    <div className="mt-4 rounded-xl border border-[#F1F5F9] bg-[#F8FAFC] p-4" data-testid="fx-trend-chart">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-[#64748B]">
          {from} → {to} · 30-day trend
        </p>
        <span
          data-testid="fx-trend-change"
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${up ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"}`}
        >
          {up ? <TrendingUp size={12} strokeWidth={2} /> : <TrendingDown size={12} strokeWidth={2} />}
          {up ? "+" : ""}{chart.change.toFixed(2)}%
        </span>
      </div>
      <svg viewBox="0 0 560 56" className="mt-2 h-14 w-full" preserveAspectRatio="none" aria-hidden="true">
        <defs>
          <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#2E7CD6" stopOpacity="0.18" />
            <stop offset="1" stopColor="#2E7CD6" stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon points={chart.area} fill="url(#trendFill)" />
        <polyline points={chart.line} fill="none" stroke="#2E7CD6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
};

const FxConverter = () => {
  const [rates, setRates] = useState(null); // null = loading, false = failed
  const [date, setDate] = useState();
  const [amount, setAmount] = useState("1,000");
  const [from, setFrom] = useState("USD");
  const [to, setTo] = useState("NGN");

  useEffect(() => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    (async () => {
      try {
        const res = await fetch(RATES_URL, { signal: controller.signal, headers: { Accept: "application/json" } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (json?.result !== "success" || !json?.rates) throw new Error("Unexpected response");
        const map = { USD: 1 };
        for (const c of CCYS) {
          const r = json.rates[c];
          if (Number.isFinite(r) && r > 0) map[c] = r;
        }
        if (Object.keys(map).length < 10) throw new Error("Missing rates");
        setRates(map);
        setDate(json.time_last_update_unix ? new Date(json.time_last_update_unix * 1000).toISOString().slice(0, 10) : undefined);
      } catch {
        setRates(false);
      } finally {
        clearTimeout(timeout);
      }
    })();
    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, []);

  if (rates === false) return null;

  const parsed = parseFloat(amount.replace(/,/g, "")) || 0;
  const result = rates ? (parsed * rates[to]) / rates[from] : null;
  const unit = rates ? rates[to] / rates[from] : null;

  const pickFrom = (v) => {
    if (v === to) setTo(from);
    setFrom(v);
  };
  const pickTo = (v) => {
    if (v === from) setFrom(to);
    setTo(v);
  };
  const swap = () => {
    setFrom(to);
    setTo(from);
  };

  return (
    <div data-testid="fx-converter" className="mt-6 rounded-2xl border border-[#E2E8F0] bg-white p-5 shadow-sm sm:p-6">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">Try a conversion</p>
        <span className="flex items-center gap-1.5 text-xs font-medium text-emerald-600">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Live reference rates
        </span>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_auto_1fr] sm:items-end">
        <div>
          <label htmlFor="fx-amount" className="mb-1.5 block text-xs font-medium text-[#64748B]">You convert</label>
          <div className="flex gap-2">
            <input
              id="fx-amount"
              data-testid="fx-amount-input"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^0-9.,]/g, ""))}
              className="w-full min-w-0 rounded-lg border border-[#E2E8F0] bg-white px-3.5 py-2 text-sm font-semibold text-[#0F172A] outline-none transition-colors focus:border-[#2E7CD6] focus:ring-2 focus:ring-[#2E7CD6]/15"
              placeholder="1,000"
            />
            <select data-testid="fx-from-select" aria-label="From currency" value={from} onChange={(e) => pickFrom(e.target.value)} className={selectCls}>
              {CCYS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <button
          data-testid="fx-swap-button"
          onClick={swap}
          aria-label="Swap currencies"
          className="mx-auto flex h-9 w-9 items-center justify-center rounded-full border border-[#E2E8F0] bg-[#F8FAFC] text-[#1E3A6E] transition-colors hover:bg-[#EFF5FD] sm:mb-0.5"
        >
          <ArrowLeftRight size={15} strokeWidth={1.5} />
        </button>

        <div>
          <label className="mb-1.5 block text-xs font-medium text-[#64748B]">They receive</label>
          <div className="flex gap-2">
            <div
              data-testid="fx-result"
              className="flex w-full min-w-0 items-center truncate rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] px-3.5 py-2 text-sm font-bold text-[#1E3A6E]"
            >
              {result === null ? "—" : fmt(result)}
            </div>
            <select data-testid="fx-to-select" aria-label="To currency" value={to} onChange={(e) => pickTo(e.target.value)} className={selectCls}>
              {CCYS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
      </div>

      <p className="mt-4 text-xs leading-relaxed text-[#94A3B8]" data-testid="fx-rate-line">
        {unit
          ? `1 ${from} = ${rateFmt(unit)} ${to} · Reference rate${date ? ` · ${date}` : ""} · Illustration only, not a trading price`
          : "Loading live rates…"}
      </p>

      <TrendChart from={from} to={to} ready={!!rates} />
    </div>
  );
};

export default FxConverter;
